import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  deleteLibrary,
  listLibrary,
  type ListLibraryParams,
  renameLibrary,
  uploadLibrary,
} from './api';

// v2 invalida cache persistido da v1 (que tinha shape `{items}` em vez de
// `{pages, pageParams}` do useInfiniteQuery — read do cache antigo crashava
// com "Cannot read properties of undefined (reading 'length')").
export const LIBRARY_KEY = ['media-library', 'v2'] as const;

export function useMediaLibrary(params: Omit<ListLibraryParams, 'cursor'> = {}) {
  return useInfiniteQuery({
    queryKey: [...LIBRARY_KEY, params],
    queryFn: ({ pageParam }) =>
      listLibrary({
        ...params,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: 30_000,
  });
}

export function useUploadLibrary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      label,
      filename,
      durationSec,
    }: {
      file: Blob;
      label: string;
      filename?: string;
      durationSec?: number;
    }) =>
      uploadLibrary(
        file,
        label,
        filename,
        durationSec !== undefined ? { durationSec } : undefined,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIBRARY_KEY });
    },
  });
}

export function useRenameLibrary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, label }: { id: number; label: string }) => renameLibrary(id, label),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIBRARY_KEY });
    },
  });
}

export function useDeleteLibrary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteLibrary(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIBRARY_KEY });
    },
  });
}
